<div class="company-portfolio-page container-fluid">
  <div class="company-portfolio-shell position-relative">
    <blocking-loader-overlay
      :show="loading_portfolio"
      :message="page_loading_message"
      :detail="page_loading_detail"
    />
    <header class="company-portfolio-header">
      <div class="company-portfolio-breadcrumb text-uppercase d-flex align-items-center gap-2">
        <span>Home</span>
        <span>/</span>
        <span>Portfolio</span>
      </div>

      <h1 class="company-portfolio-title">Portfolio &mdash; every case, every domain</h1>
    </header>

    <section class="row g-3 company-portfolio-metrics">
      <div v-for="metric in metrics" :key="metric.label" class="col-12 col-sm-6 col-xl-3">
        <article class="company-portfolio-metric h-100 shadow">
          <div class="company-portfolio-metric-label text-uppercase">{{ metric.label }}</div>
          <div class="company-portfolio-metric-value">{{ metric.value }}</div>
          <div class="company-portfolio-metric-change" :class="metric.change_class">
            {{ metric.change }}
          </div>
        </article>
      </div>
    </section>

    <section class="company-portfolio-scopes">
      <router-link :to="{ name: 'ScopeDashboard' }" class="text-decoration-none">
        <h2 class="company-portfolio-section-title">By coordinator scope</h2>
      </router-link>
      <p class="company-portfolio-section-copy">Across regions, products and case domains</p>

      <div class="company-portfolio-table-wrap shadow">
        <div class="table-responsive">
          <table class="table company-portfolio-table align-middle mb-0">
            <thead>
              <tr>
                <th class="fw-bold text-x">Scope</th>
                <th class="fw-bold">Active</th>
                <th class="fw-bold">Composition</th>
                <th class="fw-bold">SLA OK</th>
                <th class="fw-bold">Risk</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="scope in coordinator_scopes" :key="scope.id || scope.name">
                <td>
                  <div class="company-portfolio-scope-name">{{ scope.name }}</div>
                  <div class="company-portfolio-scope-meta">{{ scope.meta }}</div>
                </td>
                <td class="company-portfolio-active">{{ scope.active }}</td>
                <td>
                  <div class="d-flex flex-wrap gap-1">
                    <span
                      v-for="tag in scope.composition"
                      :key="`${scope.name}-${tag.label}`"
                      class="company-portfolio-domain-tag"
                      :class="tag.badge_class"
                    >
                      {{ tag.label }}
                    </span>
                    <span v-if="!scope.composition.length" class="company-portfolio-empty">Not configured</span>
                  </div>
                </td>
                <td class="company-portfolio-sla">{{ scope.sla_ok }}</td>
                <td>
                  <span class="company-portfolio-risk" :class="scope.risk.badge_class">
                    <span class="company-portfolio-risk-bar"></span>
                    {{ scope.risk.label }}
                  </span>
                </td>
              </tr>
              <tr v-if="portfolio_error">
                <td colspan="5" class="py-4 text-center text-secondary">{{ portfolio_error }}</td>
              </tr>
              <tr v-else-if="!loading_portfolio && !coordinator_scopes.length">
                <td colspan="5" class="py-4 text-center text-secondary">No portfolio data is available.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <section class="row g-4 company-portfolio-lower">
      <div class="col-12 col-xl-7">
        <div class="d-flex align-items-end justify-content-between gap-3 mb-3">
          <div>
            <h2 class="company-portfolio-section-title">Recent escalations</h2>
            <p class="company-portfolio-section-copy mb-0">High-risk cases needing apex visibility</p>
          </div>
          <router-link :to="{ name: 'CompanyCases' }" class="company-portfolio-view-all text-xl">
            View All &rarr;
          </router-link>
        </div>

        <div class="company-portfolio-panel shadow">
          <div class="table-responsive">
            <table class="table company-portfolio-escalations-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Case</th>
                  <th>Scope</th>
                  <th>Risk</th>
                  <th>SLA</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in recent_escalations" :key="item.case_code">
                  <td>
                    <div class="company-portfolio-case-code">{{ item.case_code }}</div>
                    <div class="company-portfolio-case-title">{{ item.title }}</div>
                  </td>
                  <td>{{ item.scope }}</td>
                  <td>
                    <span class="company-portfolio-score">
                      <span></span>{{ item.risk }}
                    </span>
                  </td>
                  <td :class="{ 'is-danger': item.sla_danger }">{{ item.sla }}</td>
                </tr>
                <tr v-if="!loading_portfolio && !recent_escalations.length">
                  <td colspan="4" class="py-4 text-center text-secondary">No recent escalations.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="col-12 col-xl-5">
        <div class="company-portfolio-panel company-portfolio-domain-panel shadow">
          <div class="d-flex align-items-center justify-content-between gap-3 mb-3">
            <div class="company-portfolio-panel-label">Portfolio by domain</div>
            <div class="company-portfolio-panel-label">{{ active_cases.length.toLocaleString('en-ZA') }} active</div>
          </div>

          <div class="company-portfolio-domain-bar mb-3">
            <span
              v-for="domain in portfolio_domains"
              :key="`${domain.name}-bar`"
              :style="{ width: `${domain.percentage}%`, backgroundColor: domain.color }"
            ></span>
          </div>

          <div class="company-portfolio-domain-grid">
            <div v-for="domain in portfolio_domains" :key="domain.name" class="company-portfolio-domain-row">
              <span class="company-portfolio-color-dot" :style="{ backgroundColor: domain.color }"></span>
              <span>{{ domain.name }}</span>
              <strong>{{ domain.active.toLocaleString('en-ZA') }}</strong>
            </div>
          </div>

          <div class="company-portfolio-panel-label company-portfolio-mix-label">Tenant mix</div>
          <div class="company-portfolio-mix-list">
            <div v-for="tenant in tenant_mix" :key="tenant.name" class="company-portfolio-mix-row">
              <span class="company-portfolio-color-dot" :style="{ backgroundColor: tenant.color }"></span>
              <span>{{ tenant.name }}</span>
              <strong>{{ tenant.active.toLocaleString('en-ZA') }}</strong>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="company-portfolio-config-section">
      <div class="d-flex flex-column flex-md-row align-items-md-end justify-content-between gap-3 mb-3">
        <div>
          <h2 class="company-portfolio-section-title">Tenant configuration</h2>
          <p class="company-portfolio-section-copy mb-0">What each tenant has customised &mdash; same shell, different shape</p>
        </div>
        <button type="button" class="company-cases-new-btn" :disabled="loading_portfolio" @click="open_tenant_configuration_editor">
          Edit configuration
        </button>
      </div>

      <div class="row g-3 mb-3">
        <div v-for="group in role_group_configs" :key="group.id || group.name" class="col-12 col-xl-4">
          <article class="company-portfolio-config-card h-100 shadow">
            <header class="d-flex align-items-center justify-content-between gap-3">
              <div class="d-flex align-items-center gap-2 min-w-0">
                <span class="company-portfolio-color-dot" :style="{ backgroundColor: group.color }"></span>
                <strong class="text-truncate">{{ group.name }}</strong>
              </div>
              <span class="company-portfolio-config-count">{{ group.active.toLocaleString('en-ZA') }} cases</span>
            </header>

            <div class="company-portfolio-config-details">
              <div v-for="detail in group.details" :key="`${group.name}-${detail.label}`" class="company-portfolio-config-row">
                <span>{{ detail.label }}</span>
                <div>{{ detail.value }}</div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  </div>

  <tenant-configuration-editor
    :open="show_tenant_configuration_editor"
    :projects="tenant_projects"
    @close="close_tenant_configuration_editor"
    @saved="refresh_tenant_configuration"
  />
</div>
